import _ from 'lodash'
import moment from 'moment'

import { Formatter } from './formatter'
import { translateString } from './utils'
import { Filter } from '../models/local-adapter/filters'
import { RELATIVE, relativeDateModesByKey } from '../models/datetime/relative-date'
import { JSONSchemaResolver } from '../resolvers/json-schema-resolver'

// ─── Types ─────────────────────────────────────────────────────────────────

/**
 * Serializable filter descriptor shared between the entity-search UI and the
 * underlying query pipeline. `type/path` drive the query; everything else
 * (label, help text, exclusiveSetKey, isVisible/isClearable) drives the
 * filter-chip UI.
 */
export interface ISearchFilterConfig {
  // Query configs
  type?: string
  path?: string
  label?: string
  value?: any
  values?: any[]
  isNegation?: boolean
  // UI configs
  isVisible?: boolean
  isClearable?: boolean
  // Help text shown inside the filter management sheet — useful when a
  // filter's implications aren't obvious from its label/value alone.
  helpText?: string
  // Mutually-exclusive groups. Filters sharing the same key can never both
  // be applied at once — adding one bumps the other back to available and
  // surfaces a "Replaced X with Y" feedback banner.
  exclusiveSetKey?: string
  // Escape hatch for filter-specific extras (e.g. RANGE start/end dates).
  [key: string]: any
}

/**
 * Row shape used by both the header pill and the management sheet. Purely
 * derived from a filter config + the entity schema — no state.
 */
export interface IDisplayFilter {
  key: string
  label: string
  value: string
  helpText?: string
  filter: ISearchFilterConfig
  identity: string
  isClearable: boolean
}

// ─── Constants ─────────────────────────────────────────────────────────────

const FILTER_TYPES = new Set<string>(Object.values(Filter))

const NEGATION_FILTER_TYPES = new Set<string>([
  Filter.NOT_MATCH,
  Filter.NOT_MATCH_EDGE,
  Filter.NOT_CONTAINS_EDGE,
])

// ─── Identity / dedup ──────────────────────────────────────────────────────

/**
 * Stable JSON key used to compare two filter configs. Order-sensitive on
 * purpose — a filter with `values: [a, b]` is not the same as `[b, a]`.
 */
export const getFilterIdentity = (filter: ISearchFilterConfig): string => {
  return JSON.stringify({
    type: filter?.type,
    path: filter?.path,
    label: filter?.label,
    value: filter?.value,
    values: filter?.values,
    isNegation: filter?.isNegation,
  })
}

export const removeFilterByIdentity = (
  filters: ISearchFilterConfig[],
  identity: string
): ISearchFilterConfig[] => {
  return _.filter(filters, (filter) => getFilterIdentity(filter) !== identity)
}

export const addFilter = (
  filters: ISearchFilterConfig[],
  filterToAdd: ISearchFilterConfig
): ISearchFilterConfig[] => {
  const identityToAdd = getFilterIdentity(filterToAdd)
  const hasFilter = _.some(filters, (filter) => getFilterIdentity(filter) === identityToAdd)
  if (hasFilter) {
    return filters
  }
  return [...filters, filterToAdd]
}

// ─── Normalization ─────────────────────────────────────────────────────────

/**
 * Applied filters from `query.filters` default to `isVisible: false` /
 * `isClearable: false` — they're baked into the query and neither shown as
 * pills nor removable unless the schema explicitly opts in.
 */
export const normalizeAutoAppliedFilter = (
  filter: ISearchFilterConfig
): ISearchFilterConfig => {
  return {
    ..._.cloneDeep(filter),
    isVisible: _.isBoolean(filter?.isVisible) ? filter.isVisible : false,
    isClearable: _.isBoolean(filter?.isClearable) ? filter.isClearable : false,
  }
}

/**
 * Available filters from `query.availableFilters` are user-selectable and
 * always visible + clearable regardless of schema flags.
 */
export const normalizeAvailableFilter = (
  filter: ISearchFilterConfig
): ISearchFilterConfig => {
  return {
    ..._.cloneDeep(filter),
    isVisible: true,
    isClearable: true,
  }
}

// ─── Exclusive-set handling ────────────────────────────────────────────────

export const getExclusiveSetKey = (
  filter: ISearchFilterConfig
): string | undefined => {
  if (!_.isString(filter?.exclusiveSetKey)) {
    return
  }
  const exclusiveSetKey = _.trim(filter.exclusiveSetKey)
  return _.isEmpty(exclusiveSetKey) ? undefined : exclusiveSetKey
}

/**
 * When two filters share the same `exclusiveSetKey`, only the first wins —
 * the rest get demoted to the available list.
 */
export const splitExclusiveSetFilters = (
  filters: ISearchFilterConfig[]
): { appliedFilters: ISearchFilterConfig[]; demotedFilters: ISearchFilterConfig[] } => {
  const appliedFilters: ISearchFilterConfig[] = []
  const demotedFilters: ISearchFilterConfig[] = []
  const activeExclusiveSetKeys = new Set<string>()

  _.forEach(filters, (filter) => {
    const exclusiveSetKey = getExclusiveSetKey(filter)

    if (_.isNil(exclusiveSetKey)) {
      appliedFilters.push(filter)
      return
    }

    if (activeExclusiveSetKeys.has(exclusiveSetKey)) {
      demotedFilters.push(filter)
      return
    }

    activeExclusiveSetKeys.add(exclusiveSetKey)
    appliedFilters.push(filter)
  })

  return { appliedFilters, demotedFilters }
}

// ─── Schema / label / value resolution ─────────────────────────────────────

export const getPropertySchema = (
  filter: ISearchFilterConfig,
  entitySchema: any,
  schemaResolver: JSONSchemaResolver
) => {
  if (!entitySchema || _.isEmpty(filter?.path)) {
    return
  }
  const result = schemaResolver.resolveSubschemaByValuePath(entitySchema, filter.path as string)
  return result?.schema
}

/**
 * Resolution order: explicit `filter.label` → schema `label`/`title` at the
 * filter's path → `_.startCase(pathTail)` → generic "Filter".
 */
export const getFilterLabel = (
  filter: ISearchFilterConfig,
  entitySchema: any,
  schemaResolver: JSONSchemaResolver
): string => {
  if (_.isString(filter?.label) && !_.isEmpty(filter.label)) {
    return filter.label
  }

  const propertySchema = getPropertySchema(filter, entitySchema, schemaResolver)
  const schemaLabel = propertySchema?.label || propertySchema?.title
  if (_.isString(schemaLabel) && !_.isEmpty(schemaLabel)) {
    return schemaLabel
  }

  if (_.isString(filter?.path) && !_.isEmpty(filter.path)) {
    const pathParts = (filter.path as string).split('.')
    return _.startCase(pathParts[pathParts.length - 1])
  }

  return 'Filter'
}

const formatRangeValue = (value: any): string | undefined => {
  if (!_.isPlainObject(value)) {
    return
  }

  const minimumValue = _.isNil(value.gte) ? value.gt : value.gte
  const maximumValue = _.isNil(value.lte) ? value.lt : value.lte

  const minimumText = _.isNil(minimumValue)
    ? undefined
    : `${_.isNil(value.gte) ? '>' : '≥'} ${minimumValue}`
  const maximumText = _.isNil(maximumValue)
    ? undefined
    : `${_.isNil(value.lte) ? '<' : '≤'} ${maximumValue}`

  const rangeText = _.compact([minimumText, maximumText]).join(', ')
  return _.isEmpty(rangeText) ? undefined : rangeText
}

const formatFilterValue = (value: any, propertySchema: any): string | undefined => {
  if (_.isNil(value)) {
    return
  }

  if (_.isArray(value)) {
    const values = _.compact(
      _.map(value, (item: any) => formatFilterValue(item, propertySchema))
    )
    const listValue = values.join(', ')
    return _.isEmpty(listValue) ? undefined : listValue
  }

  const formattedEntityValue = propertySchema
    ? Formatter.formatEntityValue(value, propertySchema, { expectString: true })
    : undefined

  if (!_.isNil(formattedEntityValue) && formattedEntityValue !== '') {
    return String(formattedEntityValue)
  }

  if (_.isBoolean(value)) {
    return Formatter.formatBoolean(value)
  }

  if (_.isPlainObject(value)) {
    const rangeText = formatRangeValue(value)
    if (!_.isNil(rangeText)) {
      return rangeText
    }
    return value.displayName || value.label || value.name
  }

  return String(value)
}

const getRelativeDateDisplayValue = (relativeDate: any): string => {
  if (!relativeDate) {
    return ''
  }

  if (relativeDate.mode === RELATIVE) {
    const offsetUnit = relativeDate.offsetUnit
    const offsetValue = parseInt(relativeDate.offsetValue, 10)
    const modeCalculator = _.get(relativeDateModesByKey as any, offsetUnit)

    if (!offsetUnit || !modeCalculator) {
      return ''
    }

    return _.lowerCase(modeCalculator.format(offsetValue))
  }

  return moment.utc(relativeDate.date).format('MMM D, YYYY')
}

const getRangeFilterValue = (filter: ISearchFilterConfig): string | undefined => {
  const hasRelativeDates = !_.isEmpty(filter?.startDate) || !_.isEmpty(filter?.endDate)

  if (hasRelativeDates) {
    const startDate = getRelativeDateDisplayValue(filter.startDate ?? filter.gte)
    const endDate = getRelativeDateDisplayValue(filter.endDate ?? filter.lte)

    if (startDate && endDate) {
      return `${startDate} - ${endDate}`
    }

    if (startDate) {
      return `after ${startDate}`
    }

    if (endDate) {
      return `before ${endDate}`
    }

    return
  }

  const startDate = filter?.gte ? moment(filter.gte).format('MMM D, YYYY') : ''
  const endDate = filter?.lte ? moment(filter.lte).format('MMM D, YYYY') : ''

  if (startDate && endDate) {
    return `${startDate} – ${endDate}`
  }

  if (startDate) {
    return `after ${startDate}`
  }

  if (endDate) {
    return `before ${endDate}`
  }
}

export const getFilterValue = (
  filter: ISearchFilterConfig,
  entitySchema: any,
  schemaResolver: JSONSchemaResolver
): string | undefined => {
  const filterType = filter?.type

  if (filterType === Filter.EXISTS) {
    return 'Is Set'
  }

  if (filterType === Filter.NOT_EXISTS) {
    return 'Is Not Set'
  }

  if (filterType === Filter.RANGE) {
    return getRangeFilterValue(filter)
  }

  const rawValue = filter?.value ?? filter?.values
  if (_.isNil(rawValue)) {
    return
  }

  const propertySchema = getPropertySchema(filter, entitySchema, schemaResolver)
  const formattedValue = formatFilterValue(rawValue, propertySchema)
  if (_.isNil(formattedValue) || formattedValue === '') {
    return
  }

  return formattedValue
}

// ─── Type predicates ───────────────────────────────────────────────────────

export const isSupportedFilterType = (filter: ISearchFilterConfig): boolean => {
  const filterType = filter?.type
  if (_.isNil(filterType)) {
    return false
  }
  return FILTER_TYPES.has(filterType)
}

export const isNegatedFilter = (filter: ISearchFilterConfig): boolean => {
  const filterType = filter?.type
  return Boolean(
    filter?.isNegation || (!_.isNil(filterType) && NEGATION_FILTER_TYPES.has(filterType))
  )
}

export const getFilterHelpText = (
  filter: ISearchFilterConfig
): string | undefined => {
  if (_.isString(filter?.helpText) && !_.isEmpty(_.trim(filter.helpText))) {
    return _.trim(filter.helpText)
  }
}

// ─── Natural-speech description ────────────────────────────────────────────

const getItemsNoun = (translations: any): string => {
  const entityDisplayNamePlural = translateString('entityDisplayName', translations, {
    count: 2,
  })
  if (
    !_.isEmpty(entityDisplayNamePlural) &&
    entityDisplayNamePlural !== 'entityDisplayName'
  ) {
    return _.lowerCase(entityDisplayNamePlural)
  }

  const translatedItems = translateString('items', translations)
  if (!_.isEmpty(translatedItems) && translatedItems !== 'items') {
    return _.lowerCase(translatedItems)
  }

  return 'items'
}

const toOrListSentenceValue = (value: string): string => {
  const values = _.toLower(value).split(', ')
  if (values.length < 3) {
    return values.join(' or ')
  }
  return `${values.slice(0, -1).join(', ')}, or ${values[values.length - 1]}`
}

/**
 * "Only show trailers where load status is empty" — auto-generated from
 * filter type + label + value + negation.
 */
export const getNaturalSpeechFilterText = (
  filter: ISearchFilterConfig,
  label: string,
  value: string,
  translations: any
): string => {
  const labelPhrase = _.lowerCase(label)
  const filterType = filter?.type
  const isNegated = isNegatedFilter(filter)
  const itemsNoun = getItemsNoun(translations)
  const sentencePrefix = `Only show ${itemsNoun}`

  const valueWithoutNegationPrefix =
    isNegated && _.startsWith(value, 'Excluding ')
      ? _.replace(value, /^Excluding\s+/, '')
      : value
  const sentenceValue = toOrListSentenceValue(valueWithoutNegationPrefix)

  if (filterType === Filter.EXISTS) {
    const withOrWithout = isNegated ? 'without' : 'with'
    return `${sentencePrefix} ${withOrWithout} ${labelPhrase}`
  }

  if (filterType === Filter.NOT_EXISTS) {
    return `${sentencePrefix} without ${labelPhrase}`
  }

  if (
    filterType === Filter.CONTAINS ||
    filterType === Filter.CONTAINS_EDGE ||
    filterType === Filter.NOT_CONTAINS_EDGE
  ) {
    return `${sentencePrefix} where ${labelPhrase} ${
      isNegated ? 'does not contain' : 'contains'
    } ${sentenceValue}`
  }

  if (filterType === Filter.RANGE) {
    const rangeValue = valueWithoutNegationPrefix
    if (
      _.startsWith(_.toLower(rangeValue), 'before ') ||
      _.startsWith(_.toLower(rangeValue), 'after ')
    ) {
      return `${sentencePrefix} where ${labelPhrase} ${rangeValue}`
    }
    return `${sentencePrefix} where ${labelPhrase} is ${rangeValue}`
  }

  if (_.isEmpty(sentenceValue)) {
    return label
  }

  return `${sentencePrefix} where ${labelPhrase} ${
    isNegated ? 'is not' : 'is'
  } ${sentenceValue}`
}

// ─── Row builder ───────────────────────────────────────────────────────────

/**
 * Filter configs → renderable rows. Skips unsupported types, invisible
 * filters, and filters whose value formats to empty. Sorted so
 * non-clearable (fixed) filters float to the top.
 */
export const buildDisplayFilters = (
  filters: ISearchFilterConfig[],
  entitySchema: any,
  schemaResolver: JSONSchemaResolver
): IDisplayFilter[] => {
  const displayFilters = _.compact(
    _.map(filters, (filter, index) => {
      if (!isSupportedFilterType(filter) || filter?.isVisible !== true) {
        return
      }

      const label = getFilterLabel(filter, entitySchema, schemaResolver)
      const rawValue = getFilterValue(filter, entitySchema, schemaResolver)
      const value =
        isNegatedFilter(filter) && !_.isEmpty(rawValue) ? `Excluding ${rawValue}` : rawValue

      if (_.isNil(value) || value === '') {
        return
      }

      const identity = getFilterIdentity(filter)
      return {
        key: `${identity}-${index}`,
        label,
        value,
        helpText: getFilterHelpText(filter),
        filter,
        identity,
        isClearable: filter?.isClearable === true,
      } as IDisplayFilter
    })
  )

  return _.sortBy(displayFilters, [
    (displayFilter) => (displayFilter.isClearable ? 1 : 0),
    (displayFilter) => _.toLower(displayFilter.label),
    (displayFilter) => _.toLower(displayFilter.value),
    (displayFilter) => displayFilter.identity,
  ])
}

/**
 * Short form used inside the "Replaced X with Y" feedback banner.
 */
export const getFilterFeedbackText = (
  filter: ISearchFilterConfig,
  entitySchema: any,
  schemaResolver: JSONSchemaResolver
): string => {
  const label = getFilterLabel(filter, entitySchema, schemaResolver)
  const rawValue = getFilterValue(filter, entitySchema, schemaResolver)
  const value =
    isNegatedFilter(filter) && !_.isEmpty(rawValue) ? `Excluding ${rawValue}` : rawValue

  if (_.isNil(value) || value === '') {
    return label
  }

  return `${label}: ${value}`
}

export const getNormalizedFiltersFromProps = (
  filters: ISearchFilterConfig[] = [],
  availableFilters: ISearchFilterConfig[] = []
): {
  appliedFilters: ISearchFilterConfig[]
  availableFilters: ISearchFilterConfig[]
} => {
  const normalizedAppliedFilters = _.map(filters, normalizeAutoAppliedFilter)
  const {
    appliedFilters: deconflictedAppliedFilters,
    demotedFilters: demotedAppliedFilters,
  } = splitExclusiveSetFilters(normalizedAppliedFilters)

  const normalizedAvailableFilters = _.map(availableFilters, normalizeAvailableFilter)
  const mergedAvailableFilters = _.reduce(
    demotedAppliedFilters,
    (nextAvailableFilters, filter) => {
      return addFilter(nextAvailableFilters, normalizeAvailableFilter(filter))
    },
    normalizedAvailableFilters
  )

  return {
    appliedFilters: deconflictedAppliedFilters,
    availableFilters: mergedAvailableFilters,
  }
}
